<template>
  <highcharts
    v-if="selectedAttributeId"
    data-id-e2e="clusterDeviationChart"
    :options="chartOptions"
  />
</template>

<script>
import { useFormatNumber } from '@/composables/use-format-number';
import { useInternationalization } from '@/composables/use-internationalization';
import colors from '@/plugins/ow-colors';
import { useClusteringStore } from '@/stores/clustering';
import { groupBy, map, mapValues, size, sumBy, uniq } from 'lodash';
import { mapState } from 'pinia';

let globalVm;

export default {
  setup() {
    const { tkey } = useInternationalization('clusteringPage.tabs.deviation');
    const { formatNumber } = useFormatNumber();

    return { tkey, formatNumber };
  },
  data() {
    const defaultX = 120;
    return {
      defaultChartOptions: {
        chart: {
          type: 'column',
          events: {
            load() {
              this.legend.title.translate(-defaultX / 2, defaultX / 5 + 1);
            },
          },
        },
        title: { text: null },
        tooltip: {
          useHTML: true,
          backgroundColor: 'white',
          borderWidth: 0,
          shadow: false,
          padding: 0,
          formatter() {
            return `<div style="background: white; padding: 6px 10px; font-size: 12px; box-shadow: 0 2px 8px ${this.series.color}; border-radius: 3px">${globalVm.tkey('chart.tooltip')} ${globalVm.formatNumber({ number: this.y, format: 'float' })}%</div>`;
          },
        },
        yAxis: {
          title: { text: this.tkey('chart.subtitle'), style: { fontSize: '12px' } },
          gridLineColor: 'transparent',
          labels: {
            style: { fontSize: '12px' },
            formatter() {
              return globalVm.formatNumber({ number: this.value, format: 'floatNonRounded' });
            },
          },
        },
        colors: colors.orderedChartColors,
        plotOptions: {
          series: {
            showCheckbox: true,
            events: {
              legendItemClick() {
                globalVm.$nextTick(() => {
                  this.checkbox.checked = this.visible;
                });
              },
            },
          },
        },
        credits: { enabled: false },
        legend: {
          align: 'left',
          symbolRadius: 0,
          symbolHeight: 0,
          symbolWidth: 0,
          squareSymbol: false,
          itemDistance: 0,
          verticalAlign: 'bottom',
          x: defaultX,
          useHTML: true,
          title: {
            text: this.tkey('chart.legendTitle'),
            style: { fontWeight: 'normal', fontSize: '12px' },
          },
          labelFormatter() {
            return `<div style="max-width: 120px;display: flex; flex-direction: row"><div style="margin-right:2px;height:19px;display:flex;justify-content:center;align-items:center;background:${this.color};"><span style="font-size:12px;letter-spacing: -0.5px;margin-bottom: 1px; color: white;font-weight:normal;margin-right:2px;margin-left:2px; padding: 2px">${this.name}</span></div></div>`;
          },
        },
      },
    };
  },
  computed: {
    ...mapState(useClusteringStore, [
      'getAttributeDeviations',
      'selectedScheme',
      'selectedAttributeId',
    ]),
    deviationsByCluster() {
      if (!size(this.getAttributeDeviations) || !this.selectedScheme) return {};

      // Global mean deviation per attribute value across all stores.
      const groupedAll = groupBy(this.getAttributeDeviations, 'attributeValue');
      const globalMean = mapValues(groupedAll, devs => sumBy(devs, 'deviation') / size(devs));
      const allAttributeValues = uniq(map(this.getAttributeDeviations, 'attributeValue'));

      // For each attribute value, build an array of per-cluster deviations in
      // selectedScheme.clusters order. Each entry is:
      //   avg(store deviations for stores in cluster) − global avg(store deviations)
      const result = Object.fromEntries(allAttributeValues.map(v => [v, []]));

      this.selectedScheme.clusters.forEach((cluster, clusterIndex) => {
        // Filter to stores in this cluster once per cluster, then group by attribute value
        // — O(n × clusters) rather than O(n × clusters × attributeValues).
        const clusterData = groupBy(
          this.getAttributeDeviations.filter(d => cluster.storeKeys.includes(d.storeKey)),
          'attributeValue'
        );

        allAttributeValues.forEach(attrValue => {
          const c = clusterData[attrValue] || [];
          result[attrValue][clusterIndex] = c.length
            ? sumBy(c, 'deviation') / c.length - globalMean[attrValue]
            : 0;
        });
      });

      return result;
    },
    orderedAttributeValues() {
      // Attribute values sorted alphabetically for a consistent, predictable x-axis order.
      // Both getAxis and getSeries consume this list — single source of truth for x-axis order.
      return [...uniq(map(this.getAttributeDeviations, 'attributeValue'))].sort((a, b) =>
        a.localeCompare(b)
      );
    },
    getAxis() {
      const attributeValues = this.orderedAttributeValues;

      return {
        categories: attributeValues,
        // lines between attribute values
        plotLines: attributeValues.map((label, i) => ({
          value: 0.5 + i,
          width: 1,
          color: '#D5D5D5',
          zIndex: 9999999,
        })),
        lineColor: 'transparent',
        labels: { style: { fontSize: '12px' } },
      };
    },
    getSeries() {
      if (!size(this.getAttributeDeviations)) return [];
      const matrix = this.deviationsByCluster;

      // Build one Highcharts series per cluster. Data points use {name, x, y} so that each
      // point is explicitly tied to its x-axis position regardless of array order (AOV3-8757).
      return this.selectedScheme.clusters.map((cluster, clusterIndex) => ({
        name: cluster.clusterName,
        selected: true,
        data: this.orderedAttributeValues.map((attributeValue, x) => ({
          name: attributeValue,
          x,
          y: (matrix[attributeValue] || [])[clusterIndex] ?? 0,
        })),
        events: {
          checkboxClick(event) {
            if (event.checked) {
              this.show();
            } else {
              this.hide();
            }
          },
        },
      }));
    },
    chartOptions() {
      return { ...this.defaultChartOptions, series: this.getSeries, xAxis: this.getAxis };
    },
  },
  created() {
    // required so we can use the vue instance in highcharts formatter function which has it's own `this` context
    globalVm = this;
  },
};
</script>

<style lang="scss" scoped>
:deep(.highcharts-container > input[type='checkbox']) {
  margin-top: 3px;
}
</style>
